import { AspectRatio, Box } from "@mantine/core"
import { auth, functions } from "@utils/firebase"
import type React from "react"
import { useEffect, useState } from "react"
import { useAuthState, useSignOut } from "react-firebase-hooks/auth"
import { useHttpsCallable } from "react-firebase-hooks/functions"
import { Link, useNavigate } from "react-router-dom"
import { ReactComponent as PennyIcon } from "../../assets/icons/penny_icon.svg"
import GotEmailIllustration from "../../assets/illustrations/got_email.png"
import ImageWithFallback from "@components/Image/ImageWithFallback"
import { useGetUser } from "@utils/hooks"
import { Timestamp } from "firebase/firestore"

type EmailVerificationProps = {}

const EmailVerification: React.FC<EmailVerificationProps> = () => {
  const [sendEmailVerification, loading, callableError] = useHttpsCallable(functions, "user-resend_confirmation_link")
  const [userAuth, userLoading, authError] = useAuthState(auth)
  const { data: userData, isLoading: userDataLoading } = useGetUser()
  const [signOut] = useSignOut(auth)
  const navigate = useNavigate()

  const [funcError, setFuncError] = useState("")
  const [funcSuccess, setFuncSuccess] = useState("")
  const [showPopup, setShowPopup] = useState(false)
  const [popupType, setPopupType] = useState("")
  const [timeRemaining, setTimeRemaining] = useState(0)

  // Handle logout and redirect
  const handleBackToEntry = async () => {
    try {
      await signOut()
      navigate("/entry")
    } catch (error) {
      console.error("Error signing out:", error)
      // Still redirect even if signout fails
      navigate("/entry")
    }
  }

  // Get localStorage key for this user
  const getStorageKey = (key: string) => {
    return `email_verification_${userAuth?.uid}_${key}`
  }

  // Get persisted timer values from localStorage
  const getPersistedTimerValues = () => {
    if (!userAuth?.uid) return { lastSentTime: null, componentMountTime: null }
    
    const lastSentTime = localStorage.getItem(getStorageKey('lastSentTime'))
    const componentMountTime = localStorage.getItem(getStorageKey('componentMountTime'))
    
    return {
      lastSentTime: lastSentTime ? parseInt(lastSentTime) : null,
      componentMountTime: componentMountTime ? parseInt(componentMountTime) : null
    }
  }

  // Set component mount time when component first loads
  useEffect(() => {
    if (!userAuth?.uid) return
    
    const { componentMountTime } = getPersistedTimerValues()
    
    if (!componentMountTime) {
      const now = Date.now()
      localStorage.setItem(getStorageKey('componentMountTime'), now.toString())
    }
  }, [userAuth?.uid])

  // Calculate time remaining based on account creation, last resend, or component mount
  const calculateTimeRemaining = () => {
    const now = Date.now()
    let earliestAllowedTime = 0
    
    // Check account creation time (60 seconds cooldown)
    if (userData?.createdAt) {
      const accountCreatedTime = new Timestamp(
        userData.createdAt.seconds, 
        userData.createdAt.nanoseconds
      ).toDate().getTime()
      earliestAllowedTime = Math.max(earliestAllowedTime, accountCreatedTime + 60000)
    }
    
    // Check last resend time (60 seconds cooldown)
    const { lastSentTime } = getPersistedTimerValues()
    if (lastSentTime) {
      earliestAllowedTime = Math.max(earliestAllowedTime, lastSentTime + 60000)
    }
    
    // Check component mount time (60 seconds cooldown from when user first arrives)
    const { componentMountTime } = getPersistedTimerValues()
    if (componentMountTime) {
      earliestAllowedTime = Math.max(earliestAllowedTime, componentMountTime + 60000)
    }
    
    return Math.max(0, Math.ceil((earliestAllowedTime - now) / 1000))
  }

  // Handle countdown timer for resend cooldown
  useEffect(() => {
    const updateTimer = () => {
      const remaining = calculateTimeRemaining()
      setTimeRemaining(remaining)
    }

    updateTimer() // Initial calculation
    
    if (timeRemaining > 0) {
      const timer = setTimeout(updateTimer, 1000)
      return () => clearTimeout(timer)
    }
  }, [userData?.createdAt, timeRemaining, userAuth?.uid])

  // Handle errors from auth state
  useEffect(() => {
    if (authError) {
      console.error("Auth error:", authError)
      setFuncError("Authentication error. Please try again.")
      setPopupType("error")
      setShowPopup(true)
    }
  }, [authError])

  // Handle errors from callable function
  useEffect(() => {
    if (callableError) {
      console.error("Callable error:", callableError)
      setFuncError("Failed to send verification email. Please try again later.")
      setPopupType("error")
      setShowPopup(true)
    }
  }, [callableError])

  const resendEmailVerification = async () => {
    try {
      setFuncError("")

      // Check if we're in the cooldown period
      const remaining = calculateTimeRemaining()
      if (remaining > 0) {
        setFuncError(`Please wait ${remaining} seconds before trying again.`)
        setPopupType("error")
        setShowPopup(true)
        return
      }

      const res = (await sendEmailVerification()) as { data: { success: boolean; error?: string } } | undefined

      if (!res || !res.data?.success) {
        setFuncError(res?.data?.error || "Failed to send verification email. Please try again.")
        setPopupType("error")
      } else {
        setFuncSuccess("Email sent successfully. Please check your inbox.")
        setPopupType("success")
        const now = Date.now()
        localStorage.setItem(getStorageKey('lastSentTime'), now.toString())
        setTimeRemaining(60)
      }

      setShowPopup(true)
    } catch (error) {
      console.error("Error sending verification email:", error)
      setFuncError("An unexpected error occurred. Please try again later.")
      setPopupType("error")
      setShowPopup(true)
    }
  }

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>

    if (showPopup) {
      timer = setTimeout(() => {
        setShowPopup(false)
        setFuncError("")
        setFuncSuccess("")
        setPopupType("")
      }, 3000)
    }

    return () => clearTimeout(timer)
  }, [showPopup])

  if (userLoading || userDataLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    )
  }

  return (
    <section className="w-full grow flex md:items-center md:justify-center">
      <div className="relative">
        {showPopup && (
          <div
            className={`fixed top-10 left-1/2 transform -translate-x-1/2 z-[100] p-2.5 rounded-lg shadow-lg text-base text-black text-center animate-fadeInOut ${popupType === "success" ? "bg-green-100" : "bg-red-100"
              }`}
          >
            {popupType === "success" ? funcSuccess : funcError}
          </div>
        )}
      </div>

      <div className="max-w-full md:max-w-[416px] md:overflow-hidden md:my-4 w-full pb-10 mx-auto md:border md:border-pn_gray-200 bg-pn_paper-1 rounded-3xl">
        {/* Back button */}
        <div className="flex items-center p-6 pb-4 border-b border-pn_gray-100">
          <button
            onClick={handleBackToEntry}
            className="flex items-center gap-x-2 text-pn_gray-600 hover:text-pn_gray-950 transition-colors duration-200 font-medium"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back
          </button>
        </div>
        <Box className="w-full relative overflow-hidden">
          <div className="rounded-2xl relative">
            <ImageWithFallback
              src={GotEmailIllustration || "/placeholder.svg"}
              alt="Verify email"
              width="100%"
              height="auto"
              loading="eager"
            />
            <div className="rounded-full bg-[#F4E34E] w-[80px] h-[80px] flex items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <PennyIcon />
            </div>
          </div>
          <div className="absolute top-[47%] left-0 right-0 w-full">
            <AspectRatio ratio={16 / 9}>
              <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="42" viewBox="0 0 375 42" fill="none">
                <path
                  d="M75.399 331.369C250.199 298.993 335.857 123.093 536.034 63.155L530.95 35.5147C323.374 75.8681 158.833 -32.7349 -6.24216 11.1731C-72.968 31.1529 -107.019 73.6584 -122.139 125.995C-146.564 210.537 -99.1907 302.739 -18.8191 325.958C9.31538 334.087 43.7863 335.321 75.399 331.369Z"
                  fill="#F8F7F3"
                />
              </svg>
            </AspectRatio>
          </div>
        </Box>

        <div className="flex flex-col gap-y-4 py-4 px-6">
          <h2 className="text-pn-large-title text-pn_gray-950">Check your email</h2>
          <span className="text-pn-body-regular !font-normal text-pn_gray-600">
            We've sent an email to <span className="text-pn-body-emphasis text-pn_gray-950">{userAuth?.email}</span>{" "}
            with a link to activate your account.
          </span>
        </div>

        <div className="pb-6 px-6 flex items-center gap-x-4">
          <Link className="btn-tertiary-sm" to={"https://mail.google.com/"}>
            Open Gmail
          </Link>
          <Link className="btn-tertiary-sm" to={"https://outlook.office365.com/mail/inbox"}>
            Open Outlook
          </Link>
        </div>

        <div className="py-4 px-6">
          <span className="text-[17px] font-inter font-normal text-pn_gray-950">
            Didn't get an email? Check your spam folder!
          </span>
        </div>

        <div className="pb-6 px-6 flex items-center gap-x-4">
          <button
            disabled={loading || timeRemaining > 0}
            onClick={resendEmailVerification}
            className="btn-tertiary-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {timeRemaining > 0 ? `Resend in ${timeRemaining}s` : "Resend confirmation link"}
          </button>
        </div>
      </div>
    </section>
  )
}
export default EmailVerification